import { FC, useReducer } from 'react';
import { useHistory } from 'react-router';
import { Link, Redirect } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';

import { BankAccountStatus, FeatureName } from '__generated__/GQL';
import {
  getBankAccountDisplayName,
  parseBankAccountIllustrationName,
} from 'common/bankAccount/utils';
import { formatAmount } from 'common/formatAmount';
import getRibUrl from 'common/getRibUrl';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineDropdown from 'components/_core/SunshineDropdown';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineSquircleIcon from 'components/_core/SunshineSquircleIcon';
import BackButton from 'components/BackButton';
import BankAccountIcon from 'components/BankAccountIcon';
import ErrorView from 'components/ErrorView';
import * as accountingRoutes from 'features/Accounting/routes';
import * as paymentCardsRoutes from 'features/PaymentCards/routes';
import { navigateToBankTransfer } from 'features/Payments/Pages/Transfers/helpers';
import * as ContentLayout from 'layouts/ContentLayout';

import * as routes from '../../routes';
import BalanceNotZeroModal from './components/BalanceNotZeroModal';
import PendingOperationsModal from './components/PendingOperationsModal';
import { BankAccountCreditCardIdsDocument } from './graphql/bankAccountCreditCardIds.gql';
import locales from './locales';

interface BankAccountProps {
  match: {
    url: string;
    params: {
      bankAccountId: string;
    };
  };
}

const BankAccount: FC<BankAccountProps> = ({
  match: {
    params: { bankAccountId },
  },
}) => {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const canAccessBank = canAccessFeature({ name: FeatureName.Bank });
  const { mainBankAccountId } = useMainBankAccountId();
  const isMainBankAccount = bankAccountId === mainBankAccountId;
  const { companyProfileId } = useCompanyContext();
  const [openPendingOperationsModal, togglePendingOperationsModal] = useReducer(
    (state) => !state,
    false,
  );
  const [openBalanceNotZeroModal, toggleBalanceNotZeroModal] = useReducer(
    (state) => !state,
    false,
  );
  const history = useHistory();
  const currentLanguage = useCurrentLanguage();
  const { bankAccounts, error: bankAccountsError } = useCompanyBankAccounts();

  const currentBankAccount = bankAccounts.find(
    (bankAccount) => bankAccount.bankAccountId === bankAccountId,
  );

  const { data: creditCardsData, error: creditCardsQueryError } = useQuery(
    BankAccountCreditCardIdsDocument,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        companyProfileId: companyProfileId as string,
        shouldQueryPaymentCards: isMainBankAccount,
      },
    },
  );

  if (!currentBankAccount || bankAccountsError || creditCardsQueryError) {
    return <ErrorView />;
  }

  if (currentBankAccount.status === BankAccountStatus.Closed) {
    return <Redirect to={routes.bankAccounts} />;
  }

  const {
    currency,
    currentBalance,
    illustrationName: baseIllustrationName,
    name,
    nextBalance,
    type,
  } = currentBankAccount;
  const illustrationName =
    parseBankAccountIllustrationName(baseIllustrationName);
  const paymentCardsCount =
    creditCardsData?.viewer?.company.creditCards.length ?? 0;

  const seePaymentCardsLinkText =
    paymentCardsCount > 1
      ? locales.seeAssociatedCards
      : locales.seeAssociatedCard;

  const balanceNotZero = currentBalance !== 0;
  const operationsPending = currentBalance !== nextBalance;

  const canEditOrCloseBankAccount = canAccessBank && !isMainBankAccount;

  const canMakeTransfer = canAccessFeature({ name: FeatureName.Payments });
  const canSeePaymentCards =
    canAccessFeature({
      name: FeatureName.PaymentcardsRead,
    }) &&
    isMainBankAccount &&
    paymentCardsCount;

  const handleCloseBankAccountClick = () => {
    if (operationsPending) {
      togglePendingOperationsModal();
    } else if (balanceNotZero) {
      toggleBalanceNotZeroModal();
    } else {
      history.push(routes.getCloseBankAccountPath(bankAccountId));
    }
  };

  const handleTransferToBeneficiaryClick = () => {
    navigateToBankTransfer(history, {
      bankTransfer: {
        fromBankAccountId: bankAccountId,
        transferType: 'external',
      },
      variant: 'fromAccountToBeneficiary',
    });
  };

  const handleTransferToWalletClick = () => {
    navigateToBankTransfer(history, {
      bankTransfer: {
        fromBankAccountId: bankAccountId,
        transferType: 'internal',
      },
      variant: 'fromAccountToAccount',
    });
  };

  const handleCreditClick = () => {
    navigateToBankTransfer(history, {
      bankTransfer: {
        toBankAccountId: bankAccountId,
        transferType: 'internal',
      },
      variant: 'toAccount',
    });
  };

  return (
    <ContentLayout.Root>
      <ContentLayout.Body>
        <VStack align="stretch" spacing="space-32">
          <BackButton onClick={() => history.push(routes.bankAccounts)} />
          <Box
            borderColor="grey.300"
            borderRadius="radius-16"
            borderWidth="1px"
            overflow="hidden"
          >
            <HStack
              align="flex-start"
              justify="space-between"
              padding="space-16"
            >
              <HStack spacing="space-24">
                <BankAccountIcon
                  illustrationName={illustrationName}
                  size="xl"
                />
                <Box>
                  <Text
                    color="grey.800"
                    fontSize="font-22"
                    fontWeight="weight-500"
                  >
                    {getBankAccountDisplayName({ name, type })}
                  </Text>
                  <Text color="grey.700" fontSize="font-18">
                    {formatAmount({
                      currency,
                      forcePlusSign: false,
                      value: currentBalance / 100,
                    })}
                  </Text>
                </Box>
              </HStack>
              {canEditOrCloseBankAccount ? (
                <Button
                  as={Link}
                  bgColor="grey.200"
                  borderRadius={50}
                  boxSize={40}
                  padding="space-12"
                  to={routes.getEditBankAccountPath(bankAccountId)}
                  variant="secondary"
                >
                  <SunshineIcon color="primary" name="edit" />
                </Button>
              ) : null}
            </HStack>
            {canMakeTransfer ? (
              <Box
                borderColor="grey.300"
                borderTopWidth="1px"
                padding="space-16"
              >
                <HStack spacing="space-16">
                  <SunshineDropdown placement="bottom-start">
                    <SunshineDropdown.Trigger>
                      <Button icon="transaction-in" variant="secondary">
                        {locales.credit}
                      </Button>
                    </SunshineDropdown.Trigger>
                    <SunshineDropdown.List>
                      <SunshineDropdown.Item
                        as="a"
                        href={getRibUrl(bankAccountId, currentLanguage)}
                        icon="transfer"
                        target="_blank"
                      >
                        {locales.creditFromIban}
                      </SunshineDropdown.Item>
                      <SunshineDropdown.Item
                        icon="transaction-out"
                        isDisabled={bankAccounts.length < 2}
                        onClick={handleCreditClick}
                      >
                        {locales.creditFromWallet}
                      </SunshineDropdown.Item>
                    </SunshineDropdown.List>
                  </SunshineDropdown>

                  <SunshineDropdown placement="bottom-end">
                    <SunshineDropdown.Trigger>
                      <Button icon="transaction-out" variant="primary">
                        {locales.transfer}
                      </Button>
                    </SunshineDropdown.Trigger>
                    <SunshineDropdown.List>
                      <SunshineDropdown.Item
                        icon="transfer"
                        isDisabled={
                          currentBalance <= 0 || bankAccounts.length < 2
                        }
                        onClick={handleTransferToWalletClick}
                      >
                        {locales.transferToWallet}
                      </SunshineDropdown.Item>
                      <SunshineDropdown.Item
                        icon="transaction-out"
                        onClick={handleTransferToBeneficiaryClick}
                      >
                        {locales.transferToBeneficiary}
                      </SunshineDropdown.Item>
                    </SunshineDropdown.List>
                  </SunshineDropdown>
                </HStack>
              </Box>
            ) : null}
          </Box>
          <Box>
            <Text color="grey.600" paddingBottom="space-8">
              {locales.accountDetails.title}
            </Text>
            <SunshineCard
              alignItems="center"
              as={Link}
              to={routes.getBankAccountDetailsPath(bankAccountId)}
            >
              <SunshineCard.Slot name="aside">
                <SunshineSquircleIcon name="wallet" />
              </SunshineCard.Slot>
              <SunshineCard.Content>
                <SunshineCard.Title>
                  {locales.accountDetails.ibanLinkTitle}
                </SunshineCard.Title>
              </SunshineCard.Content>
              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          </Box>
          {canSeePaymentCards ? (
            <Box>
              <Text color="grey.600" paddingBottom="space-8">
                {locales.paymentCards.title}
              </Text>
              <SunshineCard
                alignItems="center"
                as={Link}
                to={{
                  pathname: paymentCardsRoutes.root,
                  state: { shouldDisplayBackButton: true },
                }}
              >
                <SunshineCard.Slot name="aside">
                  <SunshineSquircleIcon name="card-recto" />
                </SunshineCard.Slot>
                <SunshineCard.Content>
                  <SunshineCard.Title>
                    {seePaymentCardsLinkText}
                  </SunshineCard.Title>
                </SunshineCard.Content>
                <SunshineCard.Slot name="action">
                  <SunshineCard.SunshineIcon name="chevron-right" />
                </SunshineCard.Slot>
              </SunshineCard>
            </Box>
          ) : null}
          <Box>
            <Text color="grey.600" paddingBottom="space-8">
              {locales.activity.title}
            </Text>
            <SunshineCard.Group>
              <SunshineCard
                alignItems="center"
                as={Link}
                to={routes.getOverviewPath(bankAccountId)}
              >
                <SunshineCard.Content>
                  <SunshineCard.Title>
                    {locales.activity.transactions}
                  </SunshineCard.Title>
                </SunshineCard.Content>
                <SunshineCard.Slot name="action">
                  <SunshineCard.SunshineIcon name="chevron-right" />
                </SunshineCard.Slot>
              </SunshineCard>
              <SunshineCard
                alignItems="center"
                as={Link}
                to={accountingRoutes.getDocumentsPath(bankAccountId)}
              >
                <SunshineCard.Content>
                  <SunshineCard.Title>
                    {locales.activity.statements}
                  </SunshineCard.Title>
                </SunshineCard.Content>
                <SunshineCard.Slot name="action">
                  <SunshineCard.SunshineIcon name="chevron-right" />
                </SunshineCard.Slot>
              </SunshineCard>
            </SunshineCard.Group>
          </Box>
          {canEditOrCloseBankAccount ? (
            <SunshineCard
              alignItems="center"
              onClick={handleCloseBankAccountClick}
            >
              <SunshineCard.Content>
                <SunshineCard.Title>
                  {locales.closeBankAccount}
                </SunshineCard.Title>
              </SunshineCard.Content>
              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          ) : null}
        </VStack>
        {operationsPending ? (
          <PendingOperationsModal
            isOpen={openPendingOperationsModal}
            onClose={togglePendingOperationsModal}
          />
        ) : null}
        {balanceNotZero ? (
          <BalanceNotZeroModal
            bankAccountId={bankAccountId}
            isOpen={openBalanceNotZeroModal}
            onClose={toggleBalanceNotZeroModal}
          />
        ) : null}
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default BankAccount;
